<script setup lang="ts">
import { ref, onMounted, onUnmounted } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'
import { useDebounce } from '../composables/useDebounce'
import { useNewsStore } from '../stores/news'
import GlowText from '../components/ui/GlowText.vue'

const { t, locale } = useI18n()
const router = useRouter()
const newsStore = useNewsStore()

const searchQuery = ref('')
const mobileMenuOpen = ref(false)
const langDropdownOpen = ref(false)
const scrolled = ref(false)

const navItems = [
  { key: 'nba', path: '/category/nba' },
  { key: 'cba', path: '/category/cba' },
  { key: 'international', path: '/category/international' },
  { key: 'equipment', path: '/category/equipment' },
  { key: 'training', path: '/category/training' },
  { key: 'culture', path: '/category/culture' },
]

const languages = [
  { code: 'zh-CN', label: '中文' },
  { code: 'en-US', label: 'English' },
  { code: 'fr-FR', label: 'Français' },
  { code: 'es-ES', label: 'Español' },
  { code: 'it-IT', label: 'Italiano' },
  { code: 'pt-BR', label: 'Português' },
  { code: 'nl-NL', label: 'Nederlands' },
  { code: 'ru-RU', label: 'Русский' },
  { code: 'pl-PL', label: 'Polski' },
  { code: 'sv-SE', label: 'Svenska' },
]

const { debouncedFn: debouncedSearch } = useDebounce((query: string) => {
  if (query.trim()) {
    router.push({ name: 'articles', query: { q: query } })
  }
}, 500)

function handleSearchInput(event: Event) {
  const target = event.target as HTMLInputElement
  searchQuery.value = target.value
  debouncedSearch(target.value)
}

function switchLanguage(code: string) {
  locale.value = code
  localStorage.setItem('locale', code)
  langDropdownOpen.value = false
}

function toggleMobileMenu() {
  mobileMenuOpen.value = !mobileMenuOpen.value
}

function closeMobileMenu() {
  mobileMenuOpen.value = false
}

function handleScroll() {
  scrolled.value = window.scrollY > 20
}

onMounted(() => {
  window.addEventListener('scroll', handleScroll, { passive: true })
  newsStore.fetchCategories()
})

onUnmounted(() => {
  window.removeEventListener('scroll', handleScroll)
})
</script>

<template>
  <header class="nav-bar" :class="{ 'nav-bar--scrolled': scrolled }">
    <div class="nav-bar__glow-line"></div>
    <div class="nav-bar__inner container">
      <router-link to="/" class="nav-bar__logo" @click="closeMobileMenu">
        <span class="nav-bar__logo-icon">🏀</span>
        <GlowText>{{ t('site.name') }}</GlowText>
      </router-link>

      <nav class="nav-bar__main-nav" :class="{ 'nav-bar__main-nav--open': mobileMenuOpen }">
        <router-link to="/" class="nav-bar__link" @click="closeMobileMenu">
          {{ t('common.home') }}
        </router-link>
        <router-link to="/articles" class="nav-bar__link" @click="closeMobileMenu">
          {{ t('common.articles') }}
        </router-link>
        <router-link
          v-for="item in navItems"
          :key="item.key"
          :to="item.path"
          class="nav-bar__link"
          @click="closeMobileMenu"
        >
          {{ t(`nav.${item.key}`) }}
        </router-link>
        <router-link to="/tags" class="nav-bar__link" @click="closeMobileMenu">
          {{ t('common.tags') }}
        </router-link>
      </nav>

      <div class="nav-bar__actions">
        <div class="nav-bar__search">
          <input
            type="text"
            :placeholder="t('common.search')"
            class="nav-bar__search-input"
            :value="searchQuery"
            @input="handleSearchInput"
          />
        </div>

        <div class="nav-bar__lang">
          <button class="nav-bar__lang-btn" @click="langDropdownOpen = !langDropdownOpen">
            🌐
          </button>
          <div v-if="langDropdownOpen" class="nav-bar__lang-dropdown">
            <button
              v-for="lang in languages"
              :key="lang.code"
              class="nav-bar__lang-option"
              :class="{ 'nav-bar__lang-option--active': locale === lang.code }"
              @click="switchLanguage(lang.code)"
            >
              {{ lang.label }}
            </button>
          </div>
        </div>

        <button class="nav-bar__mobile-btn" @click="toggleMobileMenu">
          <span></span>
          <span></span>
          <span></span>
        </button>
      </div>
    </div>
  </header>
</template>

<style scoped>
.nav-bar {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 1000;
  background: rgba(10, 14, 26, 0.85);
  backdrop-filter: blur(12px);
  transition: background 0.3s ease;
}

.nav-bar--scrolled {
  background: rgba(10, 14, 26, 0.95);
}

.nav-bar__glow-line {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: 1px;
  background: linear-gradient(90deg, transparent, var(--primary), transparent);
  opacity: 0.6;
}

.nav-bar__inner {
  display: flex;
  align-items: center;
  height: 64px;
  gap: var(--space-lg);
}

.nav-bar__logo {
  display: flex;
  align-items: center;
  gap: var(--space-sm);
  font-weight: 700;
  font-size: 1.25rem;
  white-space: nowrap;
  color: var(--text-primary);
  text-decoration: none;
}

.nav-bar__logo-icon {
  font-size: 1.5rem;
}

.nav-bar__main-nav {
  display: flex;
  gap: var(--space-sm);
  overflow-x: auto;
  flex: 1;
}

.nav-bar__link {
  color: var(--text-secondary);
  font-size: 0.875rem;
  white-space: nowrap;
  padding: var(--space-xs) var(--space-sm);
  border-radius: var(--radius-sm);
  transition: all var(--transition-fast);
  text-decoration: none;
}

.nav-bar__link:hover {
  color: var(--primary);
  background: rgba(0, 212, 255, 0.08);
}

.nav-bar__link.router-link-active {
  color: var(--primary);
  background: rgba(0, 212, 255, 0.12);
  text-shadow: 0 0 8px var(--primary-glow);
}

.nav-bar__actions {
  display: flex;
  align-items: center;
  gap: var(--space-sm);
  margin-left: auto;
}

.nav-bar__search-input {
  background: var(--bg-card);
  border: 1px solid var(--border-color);
  border-radius: var(--radius-md);
  padding: var(--space-xs) var(--space-md);
  color: var(--text-primary);
  font-size: 0.875rem;
  outline: none;
  width: 180px;
  transition: border-color var(--transition-fast), box-shadow var(--transition-fast);
}

.nav-bar__search-input:focus {
  border-color: var(--primary);
  box-shadow: 0 0 8px var(--primary-glow);
}

.nav-bar__lang {
  position: relative;
}

.nav-bar__lang-btn {
  font-size: 1.2rem;
  padding: var(--space-xs);
  border-radius: var(--radius-sm);
  transition: background var(--transition-fast);
}

.nav-bar__lang-btn:hover {
  background: rgba(0, 212, 255, 0.1);
}

.nav-bar__lang-dropdown {
  position: absolute;
  top: 100%;
  right: 0;
  margin-top: var(--space-xs);
  background: var(--bg-card);
  border: 1px solid var(--border-color);
  border-radius: var(--radius-md);
  padding: var(--space-xs);
  min-width: 140px;
  box-shadow: var(--shadow-3d-lg);
  z-index: 1001;
}

.nav-bar__lang-option {
  display: block;
  width: 100%;
  text-align: left;
  padding: var(--space-xs) var(--space-sm);
  border-radius: var(--radius-sm);
  font-size: 0.85rem;
  color: var(--text-secondary);
  transition: all var(--transition-fast);
}

.nav-bar__lang-option:hover {
  background: rgba(0, 212, 255, 0.1);
  color: var(--primary);
}

.nav-bar__lang-option--active {
  color: var(--primary);
  background: rgba(0, 212, 255, 0.12);
}

.nav-bar__mobile-btn {
  display: none;
  flex-direction: column;
  gap: 4px;
  padding: var(--space-xs);
}

.nav-bar__mobile-btn span {
  display: block;
  width: 20px;
  height: 2px;
  background: var(--text-primary);
  border-radius: 1px;
  transition: all 0.3s ease;
}

@media (max-width: 768px) {
  .nav-bar__main-nav {
    display: none;
    position: absolute;
    top: 64px;
    left: 0;
    right: 0;
    background: rgba(10, 14, 26, 0.98);
    backdrop-filter: blur(12px);
    flex-direction: column;
    padding: var(--space-md);
    border-bottom: 1px solid var(--border-color);
  }

  .nav-bar__main-nav--open {
    display: flex;
  }

  .nav-bar__mobile-btn {
    display: flex;
  }

  .nav-bar__search-input {
    width: 120px;
  }
}
</style>
